/* eslint-disable react/no-array-index-key */

import { makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { DATE_TIME_FORMAT_NO_TIME, formatDate } from 'src/modules/date-helpers';
import { WarrantyInformationDTO } from 'src/modules/generated/api';
import { equipments, getLabel } from 'src/modules/labels';
import i18n from 'src/setup/i18n';

import CarDetailHeadline from './CarDetailHeadline';
import CarDetailTableHeadCell from './CarDetailTableHeadCell';

type CarDetailWarrantyInfoPrintProps = {
  warrantyInfo?: WarrantyInformationDTO[];
  disableElevation?: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    '@media screen': {
      height: '100%',
    },
  },
  headingBox: {
    padding: theme.spacing(2),
  },
}));

const CarDetailWarrantyInfoPrint = ({ warrantyInfo, disableElevation = false }: CarDetailWarrantyInfoPrintProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const warrantyDescriptionsPresent = warrantyInfo?.some((info) => info.description !== undefined) ?? false;
  const warrantyMaxMilagePresent = warrantyInfo?.some((info) => info.maxMilage !== undefined) ?? false;
  const warrantyStartDatesPresent = warrantyInfo?.some((info) => info.startDate !== undefined) ?? false;
  const warrantyExpirationDatesPresent = warrantyInfo?.some((info) => info.expirationDate !== undefined) ?? false;

  return (
    <TableContainer component={Paper} className={classes.root} elevation={disableElevation ? 0 : 3}>
      <div className={classes.headingBox}>
        <CarDetailHeadline title={getLabel(equipments, 'WARRANTY_EXTENSION')} />
      </div>
      <Table size="small">
        <TableBody>
          {warrantyDescriptionsPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">
                {t('car.equipments.helperText.description')}
              </CarDetailTableHeadCell>
              {warrantyInfo?.map((info, idx) => (
                <TableCell key={`warranty-info-${info.description}-${idx}`}>{info.description}</TableCell>
              ))}
            </TableRow>
          )}
          {warrantyMaxMilagePresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">
                {t('car.equipments.helperText.maxMilage')}
              </CarDetailTableHeadCell>
              {warrantyInfo?.map((info, idx) => (
                <TableCell key={`warranty-max-milage-${info.maxMilage}-${idx}`}>
                  {info.maxMilage?.toLocaleString(i18n.language)} km
                </TableCell>
              ))}
            </TableRow>
          )}
          {warrantyStartDatesPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">
                {t('car.equipments.helperText.startedOn')}
              </CarDetailTableHeadCell>
              {warrantyInfo?.map((info, idx) => (
                <TableCell key={`warranty-start-date-${info.startDate}-${idx}`}>
                  {info.startDate && formatDate(info.startDate!, DATE_TIME_FORMAT_NO_TIME)}
                </TableCell>
              ))}
            </TableRow>
          )}
          {warrantyExpirationDatesPresent && (
            <TableRow>
              <CarDetailTableHeadCell className="bigger">
                {t('car.equipments.helperText.endsOn')}
              </CarDetailTableHeadCell>
              {warrantyInfo?.map((info, idx) => (
                <TableCell key={`warranty-expiration-date-${info.expirationDate}-${idx}`}>
                  {info.expirationDate && formatDate(info.expirationDate!, DATE_TIME_FORMAT_NO_TIME)}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CarDetailWarrantyInfoPrint;
